import { ColDef, ColGroupDef, GridOptions } from 'ag-grid-community'
import { OrganisationDetail, OrganisationFeature, PlatformFeature } from 'api'
import { Locale, Translation } from 'locale'
import {
  createActionsRenderer,
  createGroupedActionRenderer,
  gridOptionsBase,
  groupingRowColDef,
  StatusCellRenderer,
  TypedValueGetterParams,
  withAllContext
} from 'view/components/datagrid'
import {
  formatOrganisationTypes,
  getEntityStatusActiveSuspendedText,
  getEntityStatusColor,
  makeBooleanFormatter,
  makeBooleanQuickFilterTextGetter
} from 'view/shared/datagrid'
import { OrganisationActionsButton } from './OrganisationActionsButton'

export enum OrganisationGridColumnId {
  ORGANISATION_CHECKBOX = 'ORGANISATION_CHECKBOX',
  ORGANISATION_ACTIONS = 'ORGANISATION_ACTIONS',
  ORGANISATION_STATUS = 'ORGANISATION_STATUS',
  ORGANISATION_LEGAL_NAME = 'ORGANISATION_LEGAL_NAME',
  ORGANISATION_LEI = 'ORGANISATION_LEI',
  ORGANISATION_SHORT_CODE = 'ORGANISATION_SHORT_CODE',
  ORGANISATION_TYPES = 'ORGANISATION_TYPES',
  ORGANISATION_EMAIL_ENABLED = 'ORGANISATION_EMAIL_ENABLED',
  ORGANISATION_EMAIL = 'ORGANISATION_EMAIL',
  ORGANISATION_SFTP_ENABLED = 'ORGANISATION_SFTP_ENABLED',
  ORGANISATION_START_CONSIDERATION_OVERRIDE_ENABLED = 'ORGANISATION_START_CONSIDERATION_OVERRIDE_ENABLED',
  ORGANISATION_FIX_PERCENTAGE_FORMAT_ENABLED = 'ORGANISATION_FIX_PERCENTAGE_FORMAT_ENABLED',
  ORGANISATION_STOCK_BORROW_LENDING_ENABLED = 'ORGANISATION_STOCK_BORROW_LENDING_ENABLED',
  ORGANISATION_GFID = 'ORGANISATION_GFID',
  ORGANISATION_PRE_ASSIGNMENT = 'ORGANISATION_PRE_ASSIGNMENT'
}

export const OrganisationColumnDefs: (ColGroupDef | ColDef)[] = [
  {
    colId: OrganisationGridColumnId.ORGANISATION_CHECKBOX,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    checkboxSelection: true,
    width: 36,
    maxWidth: 36,
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockPosition: true,
    lockVisible: true,
    filter: false,
    resizable: false,
    sortable: false
  },
  groupingRowColDef,
  {
    colId: OrganisationGridColumnId.ORGANISATION_ACTIONS,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    equals: () => false,
    width: 130,
    minWidth: 130,
    headerComponent: withAllContext(createGroupedActionRenderer(OrganisationActionsButton)),
    cellClass: 'action-cell',
    cellRenderer: withAllContext(createActionsRenderer(OrganisationActionsButton)),
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockVisible: true,
    filter: false,
    sortable: false
  },
  {
    colId: OrganisationGridColumnId.ORGANISATION_STATUS,
    valueGetter: (params: TypedValueGetterParams<OrganisationDetail>) =>
      params.data && getEntityStatusActiveSuspendedText(params.data.organisation.status),
    headerName: Locale.translate(Translation.TC_STATUS),
    width: 200,
    filter: 'agSetColumnFilter',
    cellRenderer: StatusCellRenderer,
    cellRendererParams: {
      getStatusColor: (data: OrganisationDetail) => getEntityStatusColor(data.organisation.status)
    },
    equals: () => false,
    lockVisible: true
  },
  {
    colId: OrganisationGridColumnId.ORGANISATION_LEGAL_NAME,
    valueGetter: (params: TypedValueGetterParams<OrganisationDetail>) => params.data?.organisation.legalName,
    headerName: Locale.translate(Translation.TC_LEGAL_NAME),
    width: 200,
    filter: 'agSetColumnFilter'
  },
  {
    colId: OrganisationGridColumnId.ORGANISATION_SHORT_CODE,
    valueGetter: (params: TypedValueGetterParams<OrganisationDetail>) => params.data?.organisation.shortCode,
    headerName: Locale.translate(Translation.TC_SHORT_CODE),
    width: 200,
    filter: 'agSetColumnFilter'
  },
  {
    colId: OrganisationGridColumnId.ORGANISATION_LEI,
    valueGetter: (params: TypedValueGetterParams<OrganisationDetail>) => params.data?.organisation.lei,
    headerName: Locale.translate(Translation.TC_LEI),
    width: 200,
    filter: 'agSetColumnFilter'
  },
  {
    colId: OrganisationGridColumnId.ORGANISATION_TYPES,
    valueGetter: (params: TypedValueGetterParams<OrganisationDetail>) =>
      params.data && formatOrganisationTypes(params.data.organisation.organisationTypes),
    headerName: Locale.translate(Translation.TC_ORGANISATION_TYPES),
    width: 200,
    filter: 'agSetColumnFilter'
  },
  {
    colId: OrganisationGridColumnId.ORGANISATION_START_CONSIDERATION_OVERRIDE_ENABLED,
    valueGetter: (params: TypedValueGetterParams<OrganisationDetail>) =>
      params.data?.organisation.enabledFeatures.includes(PlatformFeature.ENABLE_START_CONSIDERATION_OVERRIDE),
    headerName: Locale.translate(Translation.TC_ENABLE_START_CONSIDERATION_OVERRIDE),
    width: 200,
    filter: 'agSetColumnFilter',
    valueFormatter: makeBooleanFormatter({ defaultValue: false }),
    getQuickFilterText: makeBooleanQuickFilterTextGetter({ defaultValue: false })
  },
  {
    colId: OrganisationGridColumnId.ORGANISATION_FIX_PERCENTAGE_FORMAT_ENABLED,
    valueGetter: (params: TypedValueGetterParams<OrganisationDetail>) =>
      params.data?.organisation.enabledFeatures.includes(PlatformFeature.ENABLE_FIX_PERCENTAGE_FORMAT),
    headerName: Locale.translate(Translation.TC_ENABLE_FIX_PERCENTAGE_FORMAT),
    width: 200,
    filter: 'agSetColumnFilter',
    valueFormatter: makeBooleanFormatter({ defaultValue: false }),
    getQuickFilterText: makeBooleanQuickFilterTextGetter({ defaultValue: false })
  },
  {
    colId: OrganisationGridColumnId.ORGANISATION_STOCK_BORROW_LENDING_ENABLED,
    valueGetter: (params: TypedValueGetterParams<OrganisationDetail>) =>
      params.data?.organisation.enabledFeatures.includes(PlatformFeature.ENABLE_STOCK_BORROWING_LENDING),
    headerName: Locale.translate(Translation.SC_STOCK_BORROW_LENDING),
    width: 200,
    filter: 'agSetColumnFilter',
    valueFormatter: makeBooleanFormatter({ defaultValue: false }),
    getQuickFilterText: makeBooleanQuickFilterTextGetter({ defaultValue: false })
  },
  {
    colId: OrganisationGridColumnId.ORGANISATION_GFID,
    valueGetter: (params: TypedValueGetterParams<OrganisationDetail>) => params.data?.organisation.gfidCodes.join(', '),
    headerName: Locale.translate(Translation.TC_GFID),
    width: 200,
    filter: 'agSetColumnFilter'
  },
  {
    colId: OrganisationGridColumnId.ORGANISATION_PRE_ASSIGNMENT,
    valueGetter: (params: TypedValueGetterParams<OrganisationDetail>) =>
      params.data?.organisation.enabledOrganisationFeatures.includes(OrganisationFeature.ENABLE_PRE_ASSIGNMENT),
    headerName: Locale.translate(Translation.TC_PRE_ASSIGNMENT_TOGGLE),
    width: 200,
    filter: 'agSetColumnFilter',
    valueFormatter: makeBooleanFormatter({ defaultValue: false }),
    getQuickFilterText: makeBooleanQuickFilterTextGetter({ defaultValue: false })
  }
]

export const organisationGridOptions: GridOptions = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.organisation.id
}
